import * as angular from 'angular';
import * as _ from 'lodash';
import { findIndex, mapValues, omit } from 'lodash';

export const ChartSanitizerService = function ($log) {
  const numTypes = ['number', 'integer', 'float'];
  const nonStringTypes = ['number', 'integer', 'float', 'datetime', 'date'];
  let errors = [];

  function columnExists(name, data, validTypes: string | Array<string> = undefined) {
    if (angular.isString(validTypes)) {
      validTypes = [<string>validTypes];
    } else if (angular.isUndefined(validTypes)) {
      var skipValidation = true;
    }
    if (!angular.isArray(name)) {
      name = [name];
    }
    return _.every(name, function (n: string) {
      return _.some(data.columns, function (column: any) {
        return (
          String(column.name).toLowerCase() === String(n).toLowerCase() &&
          (skipValidation || _.includes(validTypes, column.type))
        );
      });
    });
  }

  function columnsDontExist(name, data, validTypes) {
    if (angular.isString(validTypes)) {
      validTypes = [validTypes];
    } else if (angular.isUndefined(validTypes)) {
      var skipValidation = true;
    }
    if (!angular.isArray(name)) {
      name = [name];
    }
    return _.reject(name, function (n: string) {
      return _.some(data.columns, function (column: any) {
        return (
          String(column.name).toLowerCase() === String(n).toLowerCase() &&
          (skipValidation || _.includes(validTypes, column.type))
        );
      });
    });
  }

  function sanitizeChart(
    chartType,
    view,
    data,
    yValidTypes: string | Array<string> = undefined,
    xValidTypes: string | Array<string> = undefined,
    setSize = false
  ) {
    const chartView = view[chartType];
    if (chartView) {
      chartView.label = columnExists(chartView.label, data) ? chartView.label : undefined;
      chartView.g = columnExists(chartView.g, data) ? chartView.g : undefined;
      chartView.gValueColumn = columnExists(chartView.gValueColumn, data) ? chartView.gValueColumn : undefined;
      if (!chartView.g) {
        chartView.gValueColumn = undefined;
        chartView.gValueFn = undefined;
        chartView.f = undefined;
      }

      if (!columnExists(chartView.x, data, xValidTypes) && view.selectedChart === chartType) {
        errors = errors.concat(chartView.x);
      }
      if (!columnExists(chartView.y, data, yValidTypes) && view.selectedChart === chartType) {
        errors = errors.concat(columnsDontExist(chartView.y, data, yValidTypes));
      }
      chartView.x = columnExists(chartView.x, data, xValidTypes) ? chartView.x : undefined;
      chartView.y = columnExists(chartView.y, data, yValidTypes) ? chartView.y : undefined;
      if (setSize) {
        chartView.size = columnExists(chartView.size, data, yValidTypes) ? chartView.size : undefined;
      }
    }
  }

  function sanitizePieChart(view, data) {
    if (view.pie) {
      if (!columnExists(view.pie.label, data) && view.selectedChart === 'pie') {
        errors = errors.concat(view.pie.label);
      }
      if (!columnExists(view.pie.value, data, numTypes) && view.selectedChart === 'pie') {
        errors = errors.concat(columnsDontExist(view.pie.value, data, numTypes));
      }
      view.pie.label = columnExists(view.pie.label, data) ? view.pie.label : undefined;
      view.pie.value = columnExists(view.pie.value, data) ? view.pie.value : undefined;
    }
  }

  function sanitizePivotTable(view, data) {
    if (view.pivotTable) {
      const filterValueCols = view.pivotTable.filterValues != null ? Object.keys(view.pivotTable.filterValues) : [];
      const columnExistsFilter = (c) => columnExists(c, data);
      const columnDoesntExistFilter = (c) => !columnExists(c, data);
      const toOmit = filterValueCols.filter(columnDoesntExistFilter);

      // removing unnecessary columns
      ['rows', 'columns', 'values', 'filters'].forEach((attr) => {
        if (view.pivotTable[attr]) {
          view.pivotTable[attr] = view.pivotTable[attr].filter(columnExistsFilter);
        }
      });
      view.pivotTable.filterValues = omit(view.pivotTable.filterValues, toOmit);
      // filter selected column values
      view.pivotTable.filterValues = mapValues(
        view.pivotTable.filterValues,
        function filterColumnValues(colValueArr, key) {
          // filter out values that are not in content collection
          return colValueArr.filter((value) => findIndex(data.content, [key, value]) >= 0);
        }
      );
    }
  }

  return {
    sanitize: function (view, data, log) {
      if (!view || !data) {
        return;
      }

      sanitizeChart('bar', view, data, numTypes);
      sanitizeChart('line', view, data);
      sanitizeChart('linePlusBar', view, data, numTypes);
      sanitizeChart('area', view, data);
      sanitizeChart('scatter', view, data, numTypes, nonStringTypes, true);
      sanitizePieChart(view, data);
      sanitizePivotTable(view, data);

      if (errors.length) {
        view.errorMsg = '';

        _.each(_.uniq(errors), function (e) {
          view.errorMsg += 'Column "' + e + '" does not exist. ';
        });

        if (log) {
          // This used to log the errors to the console, but a customer complained about leaking the schema.
          // If we want to log these errors, which we probably don't, we can use bugsnag.
        }
      }
    },
  };
};

ChartSanitizerService.$inject = ['$log'];
